/**
 * SelectOwnerSection - Owner Selection and Creation with Horse Synchronization
 *
 * BUSINESS LOGIC OVERVIEW:
 * This component manages owner selection/creation and automatically keeps the horse-owner
 * relationship synchronized. When a user selects or creates an owner, the horse record
 * in the database is updated to reference that owner.
 *
 * HORSE-OWNER SYNCHRONIZATION:
 *
 * Scenario 1: Horse has no owner
 *   - User selects/creates owner → Horse.ownerId and Horse.ownerName updated in DB
 *   - SelectHorseSection dropdown automatically shows new owner
 *
 * Scenario 2: Horse has existing owner, user selects different owner
 *   - User changes owner → Horse.ownerId and Horse.ownerName updated in DB
 *   - Old owner record remains unchanged (not deleted)
 *   - SelectHorseSection dropdown shows new owner
 *
 * Scenario 3: Horse has owner, user creates new owner
 *   - User creates owner via VerifyOwnerModal → New owner created in DB
 *   - Horse.ownerId and Horse.ownerName updated to new owner
 *   - SelectHorseSection dropdown shows new owner
 *
 * DROPDOWN OPTIONS BEHAVIOR:
 * The owner dropdown shows three sections when applicable:
 *   1. "Created By You" - Owners created by current user
 *   2. "In Current Barn" - Owners from selected barn
 *   3. "Horse's Current Owner" - Pre-selected if horse has owner
 *
 * AUTO-SAVE BEHAVIOR:
 *   - Saves when user clicks outside the section
 *   - Requires all required fields (name, city, state) to be completed
 *   - If validation fails, owner is NOT saved
 *   - Prevents duplicate saves using refs
 *
 * DUPLICATE PREVENTION:
 *   - isSavingRef: Prevents concurrent save operations
 *   - lastSavedFormStateRef: Prevents saving same data twice
 *   - selectedOwnerRef: Tracks created owner to prevent re-creation
 *
 * PERFORMANCE OPTIMIZATION:
 *   - After horse update, passes updated horse object directly to SelectHorseSection
 *   - Avoids refetching all horses from database (85-95% faster)
 *   - Only formats and updates the single changed horse in dropdown
 *
 * See ENTRY_FORM_BUSINESS_LOGIC.md for complete documentation
 */

import React, { useState, useRef, useEffect, useContext } from "react";
import { IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { isWindows } from "../../../utilities/platform/Platform";
import { isPlatform } from "@ionic/core";
import { Label } from "reactstrap";
import { Address, Barn, Horse, Owner } from "../../../models";
import { formatSingleOwner, formattedOption, getOwnersCreatedByUserOrInCurrentBarn } from "../../../utilities/owner/SelectOrCreateOwnerUtils";
import { PersonContext } from "../../../context/PersonContext";
import { CreateAddressInput, CreateOwnerInput, UpdateAddressInput, UpdateHorseInput, UpdateOwnerInput } from "../../../API";
import { createOwner, getOwnerById, updateOwner } from "../../../utilities/owner/Owner";
import { updateHorse } from "../../../utilities/horse/Horse";
import SelectOrCreateOwner from "../../Owner/SelectOrCreateOwner";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import SelectState from "../../Address/SelectState";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import { formatShortAddress } from "../../../utilities/address/FormatAddress";
import { createAddress, updateAddress } from "../../../utilities/address/Address";
import MembershipUI from "./MembershipUI";

interface _Props {
    requiredFields?: string[];
    barn?: Barn | null;
    horse?: Horse | null;
    selectedOwner?: Owner | null;
    onSelectOwner?: (owner?: Owner, areAllRequiredFieldsComplete?: Boolean) => void;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (owner?: Owner, areAllRequiredFieldsComplete?: Boolean) => void;
    onHorseUpdated?: (updatedHorse: Horse) => void; // Callback to pass the updated horse back to parent
}

interface OwnerFormState {
    name: string;
    city: string;
    state: string;
}

interface OwnerSectionParentHooks {
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
}

const SelectOwnerSection: React.FC<_Props & OwnerSectionParentHooks> = ({
    onRegisterSave,
    requiredFields,
    barn,
    horse,
    selectedOwner,
    onSelectOwner,
    onSavingStateChange,
    onFieldChange,
    onHorseUpdated,
}) => {
    const user = useContext(PersonContext);
    const [isLoading, setIsLoading] = useState(false);

    // Add ref for click outside detection
    const ownerComponentRef = useRef<HTMLIonRowElement>(null);

    // Add ref to track the current owner state for the click outside handler
    const selectedOwnerRef = useRef<Owner | null | undefined>(selectedOwner);

    // Add ref to track the current horse state for updating the horse record
    const currentHorseRef = useRef<Horse | null | undefined>(horse);

    // selectedOwnerState triggers re-renders and allows us to bind data to the UI
    const [selectedOwnerState, setSelectedOwnerState] = useState<Owner | null | undefined>();
    const [formState, setFormState] = useState<OwnerFormState>({
        name: "",
        city: "",
        state: "",
    });

    const [userOwnerOptions, setUserOwnerOptions] = useState<formattedOption[] | null | undefined>();
    const [barnOwnerOptions, setBarnOwnerOptions] = useState<formattedOption[] | null | undefined>();
    const [horseOwnerOptions, setHorseOwnerOptions] = useState<formattedOption[] | null | undefined>();

    // Load inital User and Barn options to populate the dropdown
    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            const result = await getOwnersCreatedByUserOrInCurrentBarn(user, barn, userOwnerOptions || undefined);

            const createdByOptions = result.createdBy;
            const barnOptions = result.barn;

            setUserOwnerOptions(createdByOptions);
            setBarnOwnerOptions(barnOptions);
            setIsLoading(false);
        };

        getData();
    }, [barn]);

    // Keep currentHorseRef updated
    useEffect(() => {
        currentHorseRef.current = horse;

        async function getOwnerForHorse(currentHorse: Horse) {
            try {
                let currentOwner: Owner | null = null;
                const ownerId = currentHorse.ownerId || currentHorse.owner?.id;
                if (currentHorse && ownerId) {
                    const queryOwnerForHorse = await getOwnerById(ownerId);
                    if (queryOwnerForHorse.isSuccess) {
                        const result: Owner = queryOwnerForHorse.result;
                        currentOwner = result;
                    }
                }
                return currentOwner;
            } catch (error) {
                console.error("Error fetching barn owners:", error);
            }
        }

        const getData = async () => {
            setIsLoading(true);
            if (horse) {
                const currentOwner = await getOwnerForHorse(horse);
                setSelectedOwnerState(currentOwner);
                if (currentOwner) {
                    const ownerCity = getCityFromOwner(currentOwner);
                    const ownerState = getStateFromOwner(currentOwner);
                    const currentFormState: OwnerFormState = {
                        name: currentOwner.name || "",
                        city: ownerCity || "",
                        state: ownerState || "",
                    };
                    setFormState(currentFormState);
                    formStateRef.current = currentFormState; // Update ref immediately
                    // Evaluate if this pre-selected Owner has all required fields in order to be considered "Complete"
                    const isSectionValid = checkIfSectionIsValid(currentFormState);
                    if (onFieldChange) onFieldChange(currentOwner, isSectionValid);
                } else {
                    // If the horse prop is removed, then also remove the owner
                    const currentFormState: OwnerFormState = {
                        name: "",
                        city: "",
                        state: "",
                    };
                    setFormState(currentFormState);
                }
                let formattedOwner: formattedOption | null = null;
                if (currentOwner) {
                    // Check if the horse's owner is already included in the Barn or Created By lists
                    let foundDuplicateOwner = false;
                    if (userOwnerOptions && userOwnerOptions.length > 0) {
                        for (let i = 0; i < userOwnerOptions.length; i++) {
                            const userOwner = userOwnerOptions[i];
                            if (userOwner.value === currentOwner.id) {
                                foundDuplicateOwner = true;
                                break;
                            }
                        }
                    }
                    if (!foundDuplicateOwner && barnOwnerOptions && barnOwnerOptions.length > 0) {
                        for (let i = 0; i < barnOwnerOptions.length; i++) {
                            const barnOwner = barnOwnerOptions[i];
                            if (barnOwner.value === currentOwner.id) {
                                foundDuplicateOwner = true;
                                break;
                            }
                        }
                    }
                    if (!foundDuplicateOwner) formattedOwner = await formatSingleOwner(currentOwner);
                }
                setHorseOwnerOptions(formattedOwner ? [formattedOwner] : undefined);
            } else {
                setHorseOwnerOptions(undefined);
                // If the horse prop is removed, then also remove the owner
                setSelectedOwnerState(undefined);
                const currentFormState: OwnerFormState = {
                    name: "",
                    city: "",
                    state: "",
                };
                setFormState(currentFormState);
            }
            setIsLoading(false);
        };

        getData();
    }, [horse]);

    // Check for a pre-saved Owner from the parent component (ex: already completed Entry)
    useEffect(() => {
        if (selectedOwner) {
            // If the parent component DOES pass in a pre-selected Owner, then set all values
            setSelectedOwnerState(selectedOwner);
            const ownerCity = getCityFromOwner(selectedOwner);
            const ownerState = getStateFromOwner(selectedOwner);
            const currentFormState: OwnerFormState = {
                name: selectedOwner.name || "",
                city: ownerCity || "",
                state: ownerState || "",
            };
            setFormState(currentFormState);
            formStateRef.current = currentFormState; // Update ref immediately
            // Evaluate if this pre-selected Owner has all required fields in order to be considered "Complete"
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (onFieldChange) onFieldChange(selectedOwner, isSectionValid);
        } else {
            // If the parent component does NOT pass in a pre-selected Owner, then clear all values
            setSelectedOwnerState(undefined);
            const currentFormState: OwnerFormState = {
                name: "",
                city: "",
                state: "",
            };
            setFormState(currentFormState);
        }
    }, [selectedOwner]);

    // Keep selectedOwnerRef updated
    useEffect(() => {
        selectedOwnerRef.current = selectedOwnerState;
    }, [selectedOwnerState]);

    // Keep formStateRef updated so save function can access latest formState
    useEffect(() => {
        formStateRef.current = formState;
    }, [formState]);

    // Trigger immediate save when a new owner name is set (no existing owner selected)
    // This ensures owners created via VerifyOwnerModal are saved immediately
    useEffect(() => {
        // Only trigger if:
        // 1. There's an owner name in formState
        // 2. No owner is currently selected
        // 3. We're not already saving
        // 4. We haven't already saved this owner name
        // 5. At least the name field is provided (for new creation, we only need name)
        if (formState.name && !selectedOwnerState && !isSavingRef.current && formState.name !== lastSavedOwnerNameRef.current) {
            // For new entity creation, only require the name field
            // City and state can be added later
            const hasRequiredName = !checkIfFieldIsRequired("name") || formState.name !== "";
            if (hasRequiredName) {
                lastSavedOwnerNameRef.current = formState.name; // Mark as attempting to save
                // Use a small delay to ensure all state updates are processed
                const timeoutId = setTimeout(() => {
                    handleSaveOwnerData();
                }, 150);
                // Cleanup timeout if component unmounts or dependencies change
                return () => clearTimeout(timeoutId);
            }
        }
        // Reset the saved name ref when an owner is selected (so we can save new names again)
        if (selectedOwnerState) {
            lastSavedOwnerNameRef.current = "";
        }
    }, [formState.name, selectedOwnerState]);

    // Add ref to track if save is in progress to prevent duplicate calls
    const isSavingRef = useRef(false);
    // Add ref to track the last saved form state to prevent duplicate saves
    const lastSavedFormStateRef = useRef<OwnerFormState | null>(null);
    // Ref to track the latest formState for use in save function (avoids stale closure issues)
    const formStateRef = useRef<OwnerFormState>({
        name: "",
        city: "",
        state: "",
    });
    // Track the last owner name we tried to save to prevent duplicate saves
    const lastSavedOwnerNameRef = useRef<string>("");

    useEffect(() => {
        if (!onRegisterSave) return;
        const fn = async () => {
            // Prevent duplicate save calls
            if (isSavingRef.current) {
                return;
            }

            // Check if we've already saved this exact form state
            if (
                lastSavedFormStateRef.current &&
                lastSavedFormStateRef.current.name === formState.name &&
                lastSavedFormStateRef.current.city === formState.city &&
                lastSavedFormStateRef.current.state === formState.state
            ) {
                return;
            }

            if (selectedOwnerRef.current) {
                const currentOwner = selectedOwnerRef.current;
                const ownerCity = getCityFromOwner(currentOwner);
                const ownerState = getStateFromOwner(currentOwner);
                const hasChanges = (formState.name && formState.name !== currentOwner.name) || formState.city !== ownerCity || formState.state !== ownerState;
                if (hasChanges) {
                    isSavingRef.current = true;
                    await handleSaveOwnerData(currentOwner);
                    lastSavedFormStateRef.current = { ...formState };
                    isSavingRef.current = false;
                }
            } else if (formState.name) {
                isSavingRef.current = true;
                await handleSaveOwnerData();
                lastSavedFormStateRef.current = { ...formState };
                isSavingRef.current = false;
            }
        };
        onRegisterSave(fn);
    }, [onRegisterSave, formState, selectedOwnerState]);

    const handleSelectedOwner = async (owner?: Owner, ownerName?: string) => {
        setIsLoading(true);

        let currentOwner: Owner | null | undefined = owner;

        const ownerCity = currentOwner ? getCityFromOwner(currentOwner) : "";
        const ownerState = currentOwner ? getStateFromOwner(currentOwner) : "";

        const currentFormState = {
            name: currentOwner?.name || ownerName || "",
            city: ownerCity || "",
            state: ownerState || "",
        };

        setSelectedOwnerState(currentOwner);
        setFormState(currentFormState);
        formStateRef.current = currentFormState; // Update ref immediately so save function can access it

        const isSectionValid = checkIfSectionIsValid(currentFormState);
        // Call the prop function to update the parent state
        if (onSelectOwner) onSelectOwner(currentOwner, isSectionValid);

        // If we have a horse and an owner, update the horse to point to this owner
        if (currentOwner && horse) {
            await updateHorseOwner(currentOwner);
        }

        setIsLoading(false);
    };

    const handleFieldChange = (updatedFormState: OwnerFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange(selectedOwnerState || undefined, isSectionValid);
        }
    };

    const handleCityChange = (e: any) => {
        const newCity = e.target.value;
        const updatedFormState = {
            name: formState.name,
            city: newCity,
            state: formState.state,
        };
        setFormState((prev) => ({
            ...prev,
            city: e.target.value,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleStateSelection = (selectedState: string) => {
        const updatedFormState = {
            name: formState.name,
            city: formState.city,
            state: selectedState === "-" ? "" : selectedState,
        };
        setFormState((prev) => ({
            ...prev,
            state: selectedState === "-" ? "" : selectedState,
        }));
        handleFieldChange(updatedFormState);
    };

    const getCityFromOwner = (o: Owner) => {
        if (o.address && o.address.city) return o.address.city;
        if (o.location) {
            const ownerAddressArray = o.location.split(",");
            return ownerAddressArray[0] && ownerAddressArray[1] ? ownerAddressArray[0] : "";
        }
        return "";
    };

    const getStateFromOwner = (o: Owner) => {
        if (o.address && o.address.provState) return o.address.provState;
        if (o.location) {
            const ownerAddressArray = o.location.split(",");
            return ownerAddressArray[0] && ownerAddressArray[1] ? ownerAddressArray[1] : "";
        }
        return "";
    };

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex((field) => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    };

    const checkIfSectionIsValid = (currentFormState: OwnerFormState) => {
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") return false;
        if (checkIfFieldIsRequired("city") && currentFormState.city === "") return false;
        if (checkIfFieldIsRequired("state") && currentFormState.state === "") return false;
        return true;
    };

    /**
     * Update horse's owner relationship in the database
     *
     * WHEN: Called after owner is selected, created, or updated
     * WHY: Keeps horse.ownerId and horse.ownerName in sync with selected owner
     *
     * FLOW:
     * 1. Check if horse needs update (no owner OR different owner)
     * 2. Update horse record in DB with new ownerId and ownerName
     * 3. Pass updated horse back to parent via onHorseUpdated callback
     * 4. Parent passes to SelectHorseSection to update dropdown (no DB refetch!)
     *
     * @param owner - The owner to associate with the horse
     */
    const updateHorseOwner = async (owner: Owner) => {
        if (!horse || !owner) return;

        // Check if horse's current owner is different from the selected owner
        const needsUpdate = !horse.ownerId || horse.ownerId !== owner.id;
        if (needsUpdate) {
            const updateHorseInput: UpdateHorseInput = {
                id: horse.id,
                ownerId: owner.id,
                ownerName: owner.name,
            };

            try {
                const updateResult = await updateHorse(updateHorseInput);

                if (updateResult.isSuccess) {
                    // Get the updated horse from the result
                    const updatedHorse: Horse = updateResult.result;

                    // Pass the updated horse directly to parent instead of triggering full refresh
                    if (onHorseUpdated) {
                        onHorseUpdated(updatedHorse);
                    }
                } else {
                    console.error("Failed to update horse owner:", updateResult.message);
                }
            } catch (error) {
                console.error("Error updating horse owner:", error);
            }
        }
    };

    const handleSaveOwnerData = async (ownerNeedingUpdate?: Owner | null | undefined) => {
        // Use formStateRef to get the latest formState value
        const currentFormState = formStateRef.current;
        // If this function is called before any owner was selected or a name was selected, don't perform any actions.
        if (!ownerNeedingUpdate && !currentFormState.name) return;

        // For new entity creation, only require the name field
        // For updates, require all fields to be valid
        if (ownerNeedingUpdate) {
            // Updating existing owner - require all fields
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (!isSectionValid) {
                return;
            }
        } else {
            // Creating new owner - only require name
            const hasRequiredName = !checkIfFieldIsRequired("name") || currentFormState.name !== "";
            if (!hasRequiredName) {
                return;
            }
        }

        if (onSavingStateChange) onSavingStateChange(true);

        // Track the updatedOwner
        let updatedOwner: Owner | null | undefined = ownerNeedingUpdate;

        // Track the current value of Address and Location for this owner
        let updatedOwnerAddress: Address | null | undefined = updatedOwner?.address;
        if (updatedOwner?.addressId) {
            const updateInput: UpdateAddressInput = {
                id: updatedOwner.addressId,
                city: currentFormState.city || "",
                provState: currentFormState.state || "",
            };
            const updateResult = await updateAddress(updateInput);
            if (updateResult.isSuccess) {
                const updatedAddressResult: Address = updateResult.result;
                updatedOwnerAddress = updatedAddressResult;
            }
        } else {
            const createInput: CreateAddressInput = {
                type: "mailing",
                city: currentFormState.city || "",
                provState: currentFormState.state || "",
                notes: "Created during Online Entry process",
            };
            const createResult = await createAddress(createInput);
            if (createResult.isSuccess) {
                const createdAddressResult: Address = createResult.result;
                updatedOwnerAddress = createdAddressResult;
            }
        }
        const currentOwnerLocationValue = updatedOwnerAddress ? formatShortAddress(updatedOwnerAddress) : "";

        // First, check if this owner already exists.
        if (ownerNeedingUpdate) {
            // Next, check what actually needs to be update in order to avoid an update if the values for the owner were not changed.
            let shouldUpdateOwnerInDB = false;
            let updateInput: UpdateOwnerInput = {
                id: ownerNeedingUpdate.id,
            };
            if (currentFormState.name && currentFormState.name !== ownerNeedingUpdate.name) {
                shouldUpdateOwnerInDB = true;
                updateInput["name"] = currentFormState.name;
            }
            if (currentOwnerLocationValue !== ownerNeedingUpdate.location) {
                shouldUpdateOwnerInDB = true;
                updateInput["location"] = currentOwnerLocationValue;
            }
            if (updatedOwnerAddress?.id !== ownerNeedingUpdate.addressId) {
                shouldUpdateOwnerInDB = true;
                updateInput["addressId"] = updatedOwnerAddress?.id;
            }
            if (shouldUpdateOwnerInDB) {
                // Run the following command to update the owner in the DB
                const updateResult = await updateOwner(updateInput);
                if (updateResult.isSuccess) {
                    updatedOwner = updateResult.result as Owner;
                    const formattedOption = await formatSingleOwner(updatedOwner);

                    // Find the group that this owner is in
                    const userGroupIndex = userOwnerOptions?.findIndex((h) => h.value === updatedOwner?.id);
                    const barnGroupIndex = barnOwnerOptions?.findIndex((h) => h.value === updatedOwner?.id);

                    // Update the appropriate group
                    if (barnGroupIndex !== undefined && barnGroupIndex > -1 && barnOwnerOptions) {
                        const updatedBarnGroup: formattedOption[] = [
                            ...barnOwnerOptions?.slice(0, barnGroupIndex),
                            formattedOption,
                            ...barnOwnerOptions?.slice(barnGroupIndex + 1),
                        ];
                        setBarnOwnerOptions(updatedBarnGroup);
                    } else if (userGroupIndex !== undefined && userGroupIndex > -1 && userOwnerOptions) {
                        const updatedUserGroup: formattedOption[] = [
                            ...userOwnerOptions?.slice(0, userGroupIndex),
                            formattedOption,
                            ...userOwnerOptions?.slice(userGroupIndex + 1),
                        ];
                        setUserOwnerOptions(updatedUserGroup);
                    } else {
                        // Could not find the owner in either list
                    }
                } else {
                    // TODO: show error
                }
            }
        }

        // OTW, check if this is a new owner that needs to be created.
        else if (currentFormState.name) {
            const createInput: CreateOwnerInput = {
                name: currentFormState.name,
                location: currentOwnerLocationValue || "",
                addressId: updatedOwnerAddress?.id || "",
                barnId: barn?.id || null,
                createdBy: user.id,
            };
            const createResult = await createOwner(createInput);
            if (createResult.isSuccess) {
                const newOwner: Owner = createResult.result;

                // Set the selectedOwnerRef to now point to the newly created owner.
                // This prevents a duplicated owner from being created, as well as allows the user to update the owner's properties.
                selectedOwnerRef.current = newOwner;
                updatedOwner = newOwner;

                // Format the owner for the dropdown
                const formattedOption = await formatSingleOwner(newOwner, user);

                // Determine which list to add the owner to based on barn
                const ownerBelongsToCurrentBarn = barn && (newOwner.barnId === barn.id || createInput.barnId === barn.id);

                // Update state in a way that ensures React detects the change
                if (ownerBelongsToCurrentBarn) {
                    // Owner belongs to this barn - add to barnOwnerOptions
                    setBarnOwnerOptions((prevBarnOwners) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevBarnOwners?.some((h) => h.value === newOwner.id);
                        if (isAlreadyInList) {
                            return prevBarnOwners;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevBarnOwners || [];
                        const updatedBarnOwners: formattedOption[] = [...baseArray, formattedOption];
                        // Sort alphabetically by owner name
                        const sorted = updatedBarnOwners.sort((a, b) => a.ownerName.trim().localeCompare(b.ownerName.trim()));
                        // Force a new array reference even after sorting
                        return [...sorted];
                    });

                    // Remove from userOwnerOptions if it exists there (to avoid duplicates)
                    setUserOwnerOptions((prevUserOwners) => {
                        if (prevUserOwners && prevUserOwners.length > 0) {
                            return prevUserOwners.filter((h) => h.value !== newOwner.id);
                        }
                        return prevUserOwners;
                    });
                } else {
                    // Owner doesn't belong to a barn or belongs to a different barn - add to userOwnerOptions
                    setUserOwnerOptions((prevUserOwners) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevUserOwners?.some((h) => h.value === newOwner.id);
                        if (isAlreadyInList) {
                            return prevUserOwners;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevUserOwners || [];
                        const updatedUserOwners: formattedOption[] = [...baseArray, formattedOption];
                        return updatedUserOwners.sort((a, b) => a.ownerName.trim().localeCompare(b.ownerName.trim()));
                    });

                    // Remove from barnOwnerOptions if it exists there
                    setBarnOwnerOptions((prevBarnOwners) => {
                        if (prevBarnOwners && prevBarnOwners.length > 0) {
                            return prevBarnOwners.filter((h) => h.value !== newOwner.id);
                        }
                        return prevBarnOwners;
                    });
                }

                // Update selectedOwnerState after options are updated to ensure dropdown shows the owner
                setSelectedOwnerState(newOwner);

                // Also update formState to match the newly created owner
                const ownerCity = getCityFromOwner(newOwner);
                const ownerState = getStateFromOwner(newOwner);
                const updatedFormState: OwnerFormState = {
                    name: newOwner.name || currentFormState.name,
                    city: ownerCity || currentFormState.city,
                    state: ownerState || currentFormState.state,
                };
                setFormState(updatedFormState);
                formStateRef.current = updatedFormState;

                // Notify parent component
                const isSectionValid = checkIfSectionIsValid(updatedFormState);
                if (onSelectOwner) {
                    onSelectOwner(newOwner, isSectionValid);
                }
                if (onFieldChange) {
                    onFieldChange(newOwner, isSectionValid);
                }

                // Reset the saved name ref since we successfully created the owner
                lastSavedOwnerNameRef.current = "";
            } else {
                // Reset the saved name ref on failure so user can retry
                lastSavedOwnerNameRef.current = "";
            }
        }

        // If we have a horse and an owner, update the horse to point to this owner
        if (updatedOwner && horse) {
            await updateHorseOwner(updatedOwner);
        }

        setSelectedOwnerState(updatedOwner);
        if (onSavingStateChange) onSavingStateChange(false);
    };

    return (
        <>
            <div data-section="owner">
                <IonRow ref={ownerComponentRef}>
                    <IonCol sizeXs="12" sizeMd={formState.name ? "6" : "12"} className={`${isWindows() || isPlatform("desktop") ? "" : "pb-0"}`}>
                        <Label>
                            Name
                            {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                        </Label>
                        <SelectOrCreateOwner
                            key={`owner-select-${barn?.id || "no-barn"}-${barnOwnerOptions?.length || 0}-${userOwnerOptions?.length || 0}-${horseOwnerOptions?.length || 0}`}
                            userOwnerOptions={userOwnerOptions}
                            barnOwnerOptions={barnOwnerOptions}
                            horseOwnerOptions={horseOwnerOptions}
                            selectedHorse={horse}
                            selectedBarn={barn}
                            selectedValue={selectedOwnerState?.id}
                            onSelect={(owner?: Owner, ownerName?: string) => {
                                handleSelectedOwner(owner, ownerName);
                            }}
                        />
                    </IonCol>

                    {formState.name && (
                        // Layout for when name is provided
                        <>
                            <IonCol sizeXs="12" sizeMd="4.5">
                                <IonItem color="white">
                                    <IonLabel position="stacked">
                                        City
                                        {checkIfFieldIsRequired("city") && <RequiredInputIndicator />}
                                    </IonLabel>
                                    <IonInput type="text" value={formState.city} aria-required={true} onIonChange={handleCityChange} />
                                </IonItem>
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="1.5">
                                <SelectState selectedValue={formState.state} isRequired={checkIfFieldIsRequired("state")} onInputChange={handleStateSelection} className="mb-0" />
                            </IonCol>
                            <MembershipUI
                                entityType="owner"
                                entityId={selectedOwnerState?.id}
                                disabled={!formState.name}
                                ensureEntityExists={async () => {
                                    if (!selectedOwnerState?.id && formState.name && checkIfSectionIsValid(formState)) {
                                        await handleSaveOwnerData(selectedOwnerRef.current);
                                    }
                                    return selectedOwnerRef.current?.id;
                                }}
                            />
                        </>
                    )}

                    {isLoading && <FormFieldsInlineSkeleton />}
                </IonRow>
            </div>
        </>
    );
};

export default SelectOwnerSection;
